/**
 * LogoCropModal — frame a picked logo as a circle before it is stored.
 *
 * Every workspace logo is shown in a circular Spark Avatar, so instead of
 * squashing arbitrary pictures into that circle the upload flow pauses here:
 * the user drags the image under a round mask and zooms with the slider or
 * the −/+ buttons (`react-easy-crop`), then **Save** hands the framed square
 * to `cropLogo` (default `cropImageToSquare`), which cuts it out of the
 * original and shrinks it to the storage limits.  **Cancel** (and Escape /
 * the overlay) discards the pick and leaves the existing logo untouched.
 *
 * Owners render it with the file they got from `AvatarUpload` and receive
 * either `onSave(dataUrl)` or `onError(err)`; both close the dialog.  The
 * `useLogoCrop` hook wires the three callbacks for the common case.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Cropper from 'react-easy-crop';

import { Button, Modal, Slider, SparkIcon, SparkModalPortal, Text } from '@spark';

import { LogoImageError, cropImageToSquare } from '../utils/logoImage';
import './LogoCropModal.css';

export const LOGO_CROP_MODAL_WIDTH = 480;
export const LOGO_CROP_MIN_ZOOM = 1;
export const LOGO_CROP_MAX_ZOOM = 3;
export const LOGO_CROP_ZOOM_STEP = 0.1;

const INITIAL_CROP = { x: 0, y: 0 };

/** Snap a zoom factor onto the slider's grid inside [min, max]. */
export function clampZoom(value) {
    const steps = Math.round((Number(value) - LOGO_CROP_MIN_ZOOM) / LOGO_CROP_ZOOM_STEP);
    const snapped = LOGO_CROP_MIN_ZOOM + steps * LOGO_CROP_ZOOM_STEP;
    const clamped = Math.min(LOGO_CROP_MAX_ZOOM, Math.max(LOGO_CROP_MIN_ZOOM, snapped));
    return Math.round(clamped * 100) / 100;
}

const createObjectUrl = (file) => URL.createObjectURL(file);
const revokeObjectUrl = (url) => URL.revokeObjectURL(url);

export default function LogoCropModal({
    file = null,
    onCancel,
    onSave,
    onError,
    cropLogo = cropImageToSquare,
    objectUrl = { create: createObjectUrl, revoke: revokeObjectUrl },
}) {
    const [crop, setCrop] = useState(INITIAL_CROP);
    const [zoom, setZoom] = useState(LOGO_CROP_MIN_ZOOM);
    const [areaPixels, setAreaPixels] = useState(null);
    const [saving, setSaving] = useState(false);
    const [stateFor, setStateFor] = useState(file);

    const open = Boolean(file);

    // Every pick starts from a fresh frame.  Resetting during render (rather
    // than in an effect) means the reset is committed before the cropper
    // mounts and reports its first crop area, and a stale reset from the
    // previous dialog can never overtake a new pick.
    if (stateFor !== file) {
        setStateFor(file);
        setCrop(INITIAL_CROP);
        setZoom(LOGO_CROP_MIN_ZOOM);
        setAreaPixels(null);
        setSaving(false);
    }

    // One object URL per picked file, derived synchronously for the same
    // reason, and revoked as soon as the file changes or the dialog unmounts
    // so blobs do not pile up in memory.
    // `objectUrl` is a test seam; a new file is the only reason to recompute.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const imageUrl = useMemo(() => (file ? objectUrl.create(file) : null), [file]);
    useEffect(() => {
        if (!imageUrl) return undefined;
        return () => objectUrl.revoke(imageUrl);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [imageUrl]);

    const close = useCallback(() => {
        if (saving) return;
        onCancel?.();
    }, [onCancel, saving]);

    const fail = useCallback(
        (err) => {
            setSaving(false);
            onError?.(err);
        },
        [onError],
    );

    const save = useCallback(async () => {
        if (saving || !file || !areaPixels) return;
        setSaving(true);
        try {
            const dataUrl = await cropLogo(file, areaPixels);
            setSaving(false);
            onSave?.(dataUrl);
        } catch (err) {
            fail(err);
        }
    }, [areaPixels, cropLogo, fail, file, onSave, saving]);

    const changeZoom = useCallback((value) => setZoom(clampZoom(value)), []);
    const zoomOut = useCallback(() => setZoom((current) => clampZoom(current - LOGO_CROP_ZOOM_STEP)), []);
    const zoomIn = useCallback(() => setZoom((current) => clampZoom(current + LOGO_CROP_ZOOM_STEP)), []);
    const onCropComplete = useCallback((_area, pixels) => setAreaPixels(pixels), []);
    const onMediaError = useCallback(
        () => fail(new LogoImageError('We could not read that image. Please pick a PNG, JPEG, WEBP or GIF.')),
        [fail],
    );

    const mediaProps = useMemo(() => ({ onError: onMediaError }), [onMediaError]);

    return (
        <SparkModalPortal open={open} onClose={close} width={LOGO_CROP_MODAL_WIDTH} destroyOnClose>
            <Modal
                open
                className="logo-crop-modal"
                title="Adjust your logo"
                subtitle="Drag to reposition and zoom until the circle shows what you want."
                showSubtitle
                showOverlay={false}
                showScrollBar={false}
                fixedHeight={false}
                primaryLabel={saving ? 'Saving…' : 'Save'}
                primaryDisabled={saving || !areaPixels}
                tertiaryLabel="Cancel"
                tertiaryDisabled={saving}
                onPrimaryClick={save}
                onTertiaryClick={close}
                onCloseClick={close}
            >
                <div className="logo-crop-modal__body">
                    <div className="logo-crop-modal__stage" data-testid="logo-crop-stage">
                        {imageUrl ? (
                            <Cropper
                                image={imageUrl}
                                crop={crop}
                                zoom={zoom}
                                minZoom={LOGO_CROP_MIN_ZOOM}
                                maxZoom={LOGO_CROP_MAX_ZOOM}
                                zoomSpeed={0.5}
                                aspect={1}
                                cropShape="round"
                                showGrid={false}
                                objectFit="contain"
                                restrictPosition
                                mediaProps={mediaProps}
                                onCropChange={setCrop}
                                onZoomChange={changeZoom}
                                onCropComplete={onCropComplete}
                                classes={{
                                    containerClassName: 'logo-crop-modal__cropper',
                                    cropAreaClassName: 'logo-crop-modal__mask',
                                }}
                            />
                        ) : null}
                    </div>
                    <div className="logo-crop-modal__zoom" role="group" aria-label="Zoom">
                        <Button
                            variant="ghost"
                            size="small"
                            aria-label="Zoom out"
                            disabled={saving || zoom <= LOGO_CROP_MIN_ZOOM}
                            startIcon={<SparkIcon slug="minus" size={16} />}
                            onClick={zoomOut}
                        />
                        <Slider
                            className="logo-crop-modal__slider"
                            values={false}
                            showInput={false}
                            min={LOGO_CROP_MIN_ZOOM}
                            max={LOGO_CROP_MAX_ZOOM}
                            step={LOGO_CROP_ZOOM_STEP}
                            value={zoom}
                            disabled={saving}
                            onChange={changeZoom}
                        />
                        <Button
                            variant="ghost"
                            size="small"
                            aria-label="Zoom in"
                            disabled={saving || zoom >= LOGO_CROP_MAX_ZOOM}
                            startIcon={<SparkIcon slug="plus" size={16} />}
                            onClick={zoomIn}
                        />
                    </div>
                    <Text as="p" variant="detail-regular" color="secondary" className="logo-crop-modal__hint">
                        Only the area inside the circle is kept. Animated GIFs keep their first frame.
                    </Text>
                </div>
            </Modal>
        </SparkModalPortal>
    );
}
